import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { Button, Input, Select } from 'antd'
import { Well } from '@pushly/aqe/lib/components'
import { JourneyNode, NodeEditorRef, NodeEditorSubmitResult, ConditionOperator } from '../../types/journey-nodes'
import type { IConditionalStep } from '@pushly/models/lib/structs/journeys'
import { updateStepConfig } from '@pushly/models/lib/structs/journeys'
import { JourneyBuilderMode } from '../../enums/journey-builder-mode.enum'
import { useJourneyContext } from '../../context'
import { JourneyAction } from '../../types/journey-context'
import { validateConditionStepFields } from '../../utils/graph/validation'
import './condition-step-editor.scss'

const OPERATOR_OPTIONS: { value: ConditionOperator; label: string }[] = [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Does not equal' },
    { value: 'contains', label: 'Contains' },
    { value: 'greater_than', label: 'Greater than' },
    { value: 'less_than', label: 'Less than' },
]

type ConditionStepEditorProps = {
    node: JourneyNode<IConditionalStep>
}

export const ConditionStepEditor = forwardRef<NodeEditorRef, ConditionStepEditorProps>(({ node }, ref) => {
    const [state, dispatch] = useJourneyContext()
    const config = node.data.step.configuration

    const [field, setField] = useState(config.condition.field ?? '')
    const [operator, setOperator] = useState(config.condition.operator ?? '')
    const [value, setValue] = useState<string>(String(config.condition.value ?? ''))

    const isReadonly = state.mode === JourneyBuilderMode.READONLY

    const validation = useMemo(
        () => validateConditionStepFields({ field, operator }),
        [field, operator],
    )

    const hasErrors = !validation.valid

    // Manage submit button in footer
    useEffect(() => {
        if (isReadonly) return

        dispatch({
            action: JourneyAction.SET_EDITOR_FOOTER,
            data: (
                <Button
                    type="primary"
                    disabled={hasErrors}
                    onClick={async () => {
                        const result = await (ref as React.MutableRefObject<NodeEditorRef>)?.current?.onSubmit()
                        if (result) {
                            dispatch({ action: JourneyAction.ON_NODE_STEP_CHANGE, data: result as { nodeId: string; step: any } })
                            dispatch({ action: JourneyAction.SET_ACTIVE_NODE, data: null })
                        }
                    }}
                >
                    Update Step
                </Button>
            ),
        })
    }, [hasErrors, isReadonly, dispatch])

    // Cleanup footer on unmount
    useEffect(() => {
        return () => {
            dispatch({ action: JourneyAction.SET_EDITOR_FOOTER, data: undefined })
        }
    }, [dispatch])

    useImperativeHandle(ref, () => ({
        onSubmit: (): NodeEditorSubmitResult => {
            return {
                nodeId: node.id,
                step: updateStepConfig(node.data.step, {
                    condition: {
                        field,
                        operator: operator as ConditionOperator,
                        value: value || null,
                    },
                }),
            }
        },
    }))

    const fieldError = validation.errors.find(e => e.field === 'condition.field')
    const operatorError = validation.errors.find(e => e.field === 'condition.operator')

    return (
        <div className="condition-step-editor">
            <Well mode="ghost" title="Condition" hideFooter={true}>
                <div className="condition-field-row">
                    <label className="condition-label">Field</label>
                    <Input
                        placeholder="e.g. user.country"
                        value={field}
                        disabled={isReadonly}
                        status={fieldError ? 'error' : undefined}
                        onChange={(e) => setField(e.target.value)}
                    />
                    {fieldError && <div className="field-error">{fieldError.message}</div>}
                </div>

                <div className="condition-field-row">
                    <label className="condition-label">Operator</label>
                    <Select
                        placeholder="Select operator"
                        value={operator || undefined}
                        disabled={isReadonly}
                        status={operatorError ? 'error' : undefined}
                        onChange={(val) => setOperator(val)}
                        options={OPERATOR_OPTIONS}
                        style={{ width: '100%' }}
                    />
                    {operatorError && <div className="field-error">{operatorError.message}</div>}
                </div>

                <div className="condition-field-row">
                    <label className="condition-label">Value</label>
                    <Input
                        placeholder="Comparison value"
                        value={value}
                        disabled={isReadonly}
                        onChange={(e) => setValue(e.target.value)}
                    />
                </div>
            </Well>
        </div>
    )
})
